import { useCallback, useEffect, useRef, useState } from "react";
import type { User } from "firebase/auth";
import { resolveAudioRefRefreshCutoff } from "@mobile/audio/audioRefreshPolicy";
import { LockKeyhole, Play, X } from "lucide-react";
import { HousePromotionDialog } from "./HousePromotionDialog";

import type { SupportLanguage } from "../appModel";
import { callAuthenticatedWordinariusFunction, callPublicWordinariusFunction } from "../platform/firebaseFunctions";
import { beginWebAudioPlaybackRequest, type WebAudioPlaybackLease } from "../platform/webAudio";
import type { WebMonetizationProfile } from "./webMonetization";
import {
  getHousePromotion,
  markHousePromotionShown,
  preloadHousePromotionCatalog,
  type HousePromotionView,
} from "./housePromotions";
import { getWebAudioRewardCopy } from "./webAudioCopy";
import {
  getRewardedAdAvailability,
  isRewardedProviderUnavailable,
  preloadRewardedAdProvider,
  showRewardedAd,
  type ConsentState,
} from "./rewardedAds";
import type { WebAudioUnitType } from "./webAudioUnit";

export type { WebAudioUnitType } from "./webAudioUnit";

export interface WebRemoteAudioRequest {
  targetLanguage: string;
  level: string;
  domain: string;
  scenarioId: string;
  unitType: WebAudioUnitType;
  unitId: string;
  audioRef: string;
  audioRefs: string[];
}

interface AuthorizedAudioResponse {
  downloadUrl: string;
  downloadUrlExpiresAt: string;
}

interface AuthorizedAudioBatchResponse {
  downloads: Array<AuthorizedAudioResponse & {
    audioRef: string;
    normalizedAudioRef: string;
  }>;
}

interface BeginRewardResponse {
  sessionId: string;
}

type DialogState = "checking" | "sign_in" | "reward" | "working" | "provider_fallback" | "house_promo" | "not_completed" | "error";
type PendingPurpose = "play" | "prepare";

const COPY: Record<SupportLanguage, {
  close: string;
  checkingBody: string;
  checkingTitle: string;
  error: string;
  errorBody: string;
  housePromoContinue: string;
  housePromoLabel: string;
  notCompleted: string;
  notCompletedBody: string;
  providerFallback: string;
  providerFallbackBody: string;
  openProfile: string;
  preparing: string;
  signInBody: string;
  signInTitle: string;
  tryAgain: string;
}> = {
  en: {
    close: "Close",
    checkingBody: "Checking whether this audio package is already available on this device",
    checkingTitle: "Checking audio access",
    error: "Audio access could not be prepared",
    errorBody: "The section was not opened because its audio package is not ready. Check your connection and available storage, then retry the download",
    housePromoContinue: "Continue",
    housePromoLabel: "From our apps",
    notCompleted: "The ad was not completed",
    notCompletedBody: "The audio package was not opened. You can try again when you are ready",
    providerFallback: "Preparing the audio package",
    providerFallbackBody: "No ad is available right now. Your audio package is opening at no charge",
    openProfile: "Open profile",
    preparing: "Preparing the ad...",
    signInBody: "Sign in first so this protected audio and your progress stay linked to your account",
    signInTitle: "Sign in required",
    tryAgain: "Try again",
  },
  tr: {
    close: "Kapat",
    checkingBody: "Bu ses paketinin cihazda hazır olup olmadığı kontrol ediliyor",
    checkingTitle: "Ses erişimi kontrol ediliyor",
    error: "Ses erişimi hazırlanamadı",
    errorBody: "Ses paketi hazır olmadığı için bölüm açılmadı. Bağlantını ve boş depolama alanını kontrol edip indirmeyi tekrar dene",
    housePromoContinue: "Devam et",
    housePromoLabel: "Diğer uygulamalarımız",
    notCompleted: "Reklam tamamlanmadı",
    notCompletedBody: "Ses paketi açılmadı. Hazır olduğunda tekrar deneyebilirsin",
    providerFallback: "Ses paketi hazırlanıyor",
    providerFallbackBody: "Şu anda uygun reklam yok. Ses paketin ücretsiz açılıyor",
    openProfile: "Profili aç",
    preparing: "Reklam hazırlanıyor...",
    signInBody: "Korumalı seslerin ve ilerlemenin hesabına bağlı kalması için önce giriş yap",
    signInTitle: "Giriş gerekli",
    tryAgain: "Tekrar dene",
  },
  ar: {
    close: "إغلاق",
    checkingBody: "يجري التحقق مما إذا كانت حزمة الصوت جاهزة على هذا الجهاز",
    checkingTitle: "جار التحقق من الوصول إلى الصوت",
    error: "تعذر تجهيز الوصول إلى الصوت",
    errorBody: "لم يُفتح القسم لأن حزمة الصوت غير جاهزة. تحقق من الاتصال والمساحة المتاحة ثم أعد التنزيل",
    housePromoContinue: "متابعة",
    housePromoLabel: "من تطبيقاتنا",
    notCompleted: "لم يكتمل الإعلان",
    notCompletedBody: "لم تُفتح حزمة الصوت. يمكنك المحاولة مرة أخرى عندما تكون مستعدا",
    providerFallback: "جار تجهيز حزمة الصوت",
    providerFallbackBody: "لا يتوفر إعلان مناسب الآن. تُفتح حزمة الصوت مجانا",
    openProfile: "فتح الملف الشخصي",
    preparing: "جار تجهيز الإعلان...",
    signInBody: "سجل الدخول أولا لربط الصوت المحمي وتقدمك بحسابك",
    signInTitle: "تسجيل الدخول مطلوب",
    tryAgain: "حاول مرة أخرى",
  },
};

const AUDIO_CACHE = "wordinarius-echo-protected-audio-v1";

export function buildWebAudioCacheKey(audioRef: string, origin = window.location.origin): string {
  const repairCutoff = resolveAudioRefRefreshCutoff(audioRef);
  const revision = repairCutoff ? `?repair=${encodeURIComponent(repairCutoff)}` : "";
  return `${origin}/app/offline-audio/${encodeURIComponent(audioRef)}${revision}`;
}

export function getAudioPreparationCopy(language: SupportLanguage) {
  return COPY[language];
}

function audioCacheKey(audioRef: string): string {
  return buildWebAudioCacheKey(audioRef);
}

class AudioPreparationError extends Error {}

const sessionAudio = new Map<string, Blob>();
const SESSION_AUDIO_BUDGET = 64 * 1024 * 1024;
let sessionAudioBytes = 0;

export function resetSessionAudioForTests() {
  sessionAudio.clear();
  sessionAudioBytes = 0;
}

async function rememberAudio(audioRef: string, response: Response): Promise<boolean> {
  const blob = await response.blob();
  if (blob.size > SESSION_AUDIO_BUDGET) return false;
  const key = audioCacheKey(audioRef);
  const previous = sessionAudio.get(key);
  if (previous) sessionAudioBytes -= previous.size;
  sessionAudio.delete(key);
  while (sessionAudioBytes + blob.size > SESSION_AUDIO_BUDGET) {
    const oldest = sessionAudio.keys().next().value;
    if (!oldest) break;
    sessionAudioBytes -= sessionAudio.get(oldest)!.size;
    sessionAudio.delete(oldest);
  }
  sessionAudio.set(key, blob);
  sessionAudioBytes += blob.size;
  return true;
}

async function usableAudioResponse(response: Response): Promise<boolean> {
  const type = response.headers.get("content-type")?.split(";")[0]?.trim() || "";
  return response.ok && (type.startsWith("audio/") || type === "application/octet-stream")
    && (await response.clone().blob()).size > 0;
}

async function readCachedAudio(audioRef: string): Promise<Response | null> {
  const memory = sessionAudio.get(audioCacheKey(audioRef));
  if (memory) return new Response(memory, { headers: { "Content-Type": memory.type } });
  if (!("caches" in window)) return null;
  try {
    const response = await caches.match(audioCacheKey(audioRef));
    if (!response) return null;
    if (await usableAudioResponse(response)) return response;
    const cache = await caches.open(AUDIO_CACHE);
    await cache.delete(audioCacheKey(audioRef));
  } catch {
    // Managed/private browsers may deny persistence; downloaded session bytes still work.
  }
  return null;
}

async function missingCachedAudio(audioRefs: string[]): Promise<string[]> {
  const missing: string[] = [];
  for (let start = 0; start < audioRefs.length; start += 8) {
    const batch = audioRefs.slice(start, start + 8);
    const states = await Promise.all(batch.map(async (ref) => Boolean(await readCachedAudio(ref))));
    missing.push(...batch.filter((_ref, index) => !states[index]));
  }
  return missing;
}

async function playCachedAudio(audioRef: string, playback: WebAudioPlaybackLease): Promise<boolean | null> {
  const response = await readCachedAudio(audioRef);
  if (!response) return null;
  const url = URL.createObjectURL(await response.blob());
  try {
    return await playback.play(url);
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function cacheAuthorizedAudio(audioRef: string, downloadUrl: string): Promise<boolean> {
  try {
    const response = await fetch(downloadUrl);
    if (!await usableAudioResponse(response)) return false;
    const remembered = await rememberAudio(audioRef, response.clone());
    try {
      const cache = await caches.open(AUDIO_CACHE);
      await cache.put(audioCacheKey(audioRef), response);
      return true;
    } catch {
      return remembered;
    }
  } catch {
    return false;
  }
}

function backendAudioRequest(request: WebRemoteAudioRequest, audioRef: string) {
  const { audioRefs: _audioRefs, ...unit } = request;
  return { ...unit, audioRef };
}

function backendAudioBatchRequest(request: WebRemoteAudioRequest, audioRefs: string[]) {
  const { audioRef: _audioRef, audioRefs: _audioRefs, ...unit } = request;
  return { ...unit, audioRefs };
}

async function cacheAndPlayAudio(audioRef: string, downloadUrl: string, playback: WebAudioPlaybackLease): Promise<boolean> {
  if (!("caches" in window)) {
    return playback.play(downloadUrl);
  }
  let url: string;
  try {
    const response = await fetch(downloadUrl);
    if (!await usableAudioResponse(response)) throw new Error("audio_download_failed");
    await rememberAudio(audioRef, response.clone());
    const cache = await caches.open(AUDIO_CACHE);
    await cache.put(audioCacheKey(audioRef), response.clone());
    url = URL.createObjectURL(await response.blob());
  } catch {
    return playback.play(downloadUrl);
  }
  try {
    return await playback.play(url);
  } finally {
    URL.revokeObjectURL(url);
  }
}

function readRewardedAdUnitPath(): string | null {
  const value = String(import.meta.env.VITE_GOOGLE_AD_MANAGER_REWARDED_UNIT_PATH || "").trim();
  if (!value || value.includes("ca-app-pub-") || !value.startsWith("/")) return null;
  return value;
}

export function useWebAudioAccess(params: {
  consent: ConsentState;
  monetization: WebMonetizationProfile;
  onOpenProfile: () => void;
  supportLanguage: SupportLanguage;
  user: User | null;
}) {
  const [pending, setPending] = useState<WebRemoteAudioRequest | null>(null);
  const [pendingPurpose, setPendingPurpose] = useState<PendingPurpose>("play");
  const [dialogState, setDialogState] = useState<DialogState | null>(null);
  const [housePromotion, setHousePromotion] = useState<HousePromotionView | null>(null);
  const [lastRewardedUnlockAtMs, setLastRewardedUnlockAtMs] = useState<number | null>(null);
  const [preparationError, setPreparationError] = useState<string | undefined>();
  const pendingResolver = useRef<((granted: boolean) => void) | null>(null);
  const pendingPlayback = useRef<WebAudioPlaybackLease | null>(null);
  const unlockAttempt = useRef(0);
  const copy = COPY[params.supportLanguage];
  const adUnitPath = readRewardedAdUnitPath();

  useEffect(() => {
    if (
      !adUnitPath
      || !params.user
      || params.consent === "unknown"
      || params.monetization.source !== "backend"
      || !params.monetization.adsEnabled
      || params.monetization.subscriptionTier === "premium"
    ) return;
    void preloadRewardedAdProvider({
      adUnitPath,
      consent: params.consent,
      isPremium: false,
    });
    void preloadHousePromotionCatalog();
  }, [adUnitPath, params.consent, params.monetization.adsEnabled, params.monetization.source, params.monetization.subscriptionTier, params.user]);

  const authorizeAndPlay = useCallback(async (request: WebRemoteAudioRequest, playback: WebAudioPlaybackLease | null) => {
    if (!playback?.isCurrent()) return false;
    const cachedPlayback = await playCachedAudio(request.audioRef, playback);
    if (cachedPlayback !== null) return cachedPlayback;
    if (!playback.isCurrent()) return false;
    const response = await callPublicWordinariusFunction<AuthorizedAudioResponse>(
      "authorizeRemoteAudioDownload",
      backendAudioRequest(request, request.audioRef),
    );
    if (!response.downloadUrl) throw new Error("audio_url_missing");
    return cacheAndPlayAudio(request.audioRef, response.downloadUrl, playback);
  }, []);

  const authorizeAndPrepare = useCallback(async (request: WebRemoteAudioRequest) => {
    const audioRefs = Array.from(new Set(
      (request.audioRefs.length > 0 ? request.audioRefs : [request.audioRef]).filter(Boolean),
    ));
    if (!audioRefs.length) throw new AudioPreparationError("audio_pack_empty");
    const pendingAudioRefs = await missingCachedAudio(audioRefs).catch(() => {
      throw new AudioPreparationError("audio_cache_unavailable");
    });
    if (pendingAudioRefs.length === 0) return true;

    const response = await callPublicWordinariusFunction<AuthorizedAudioBatchResponse>(
      "authorizeRemoteAudioDownload",
      backendAudioBatchRequest(request, pendingAudioRefs),
    );
    const downloads = Array.isArray(response.downloads) ? response.downloads : [];
    const downloadByRef = new Map(downloads.map((download) => [download.audioRef, download]));
    if (pendingAudioRefs.some((audioRef) => !downloadByRef.get(audioRef)?.downloadUrl)) {
      throw new AudioPreparationError("audio_batch_incomplete");
    }

    for (let start = 0; start < pendingAudioRefs.length; start += 8) {
      const batch = pendingAudioRefs.slice(start, start + 8);
      const results = await Promise.all(batch.map((audioRef) => {
        const download = downloadByRef.get(audioRef);
        if (!download?.downloadUrl) throw new Error("audio_url_missing");
        return cacheAuthorizedAudio(audioRef, download.downloadUrl);
      }));
      if (results.some((cached) => !cached)) throw new AudioPreparationError("audio_cache_failed");
    }
    const missing = await missingCachedAudio(audioRefs).catch(() => {
      throw new AudioPreparationError("audio_cache_unavailable");
    });
    if (missing.length) throw new AudioPreparationError("audio_pack_incomplete");
    return true;
  }, []);

  const settlePending = useCallback((granted: boolean) => {
    pendingResolver.current?.(granted);
    pendingResolver.current = null;
  }, []);

  const closeDialog = useCallback(() => {
    unlockAttempt.current += 1;
    pendingPlayback.current?.cancel();
    pendingPlayback.current = null;
    settlePending(false);
    setPending(null);
    setHousePromotion(null);
    setDialogState(null);
  }, [settlePending]);

  useEffect(() => () => {
    unlockAttempt.current += 1;
    pendingPlayback.current?.cancel();
    pendingPlayback.current = null;
    settlePending(false);
  }, [settlePending]);

  const requestPlay = useCallback(async (request: WebRemoteAudioRequest): Promise<boolean> => {
    const playback = beginWebAudioPlaybackRequest();
    pendingPlayback.current = playback;
    try {
      return await authorizeAndPlay(request, playback);
    } catch (error) {
      if (!playback.isCurrent()) return false;
      if (error instanceof Error && /^audio_playback_(failed|timeout)$/.test(error.message)) throw error;
      setPending(request);
      setPendingPurpose("play");
      if (!params.user) {
        setDialogState("sign_in");
      } else if (params.monetization.subscriptionTier === "premium") {
        setDialogState("error");
      } else if (params.monetization.source !== "backend" || !params.monetization.adsEnabled) {
        setDialogState("error");
      } else {
        setDialogState("reward");
      }
      return false;
    }
  }, [authorizeAndPlay, params.monetization.adsEnabled, params.monetization.source, params.monetization.subscriptionTier, params.user]);

  const attemptPreparation = useCallback((request: WebRemoteAudioRequest, attempt: number) => {
    setPreparationError(undefined);
    setDialogState("checking");
    void authorizeAndPrepare(request)
        .then((granted) => {
          if (attempt !== unlockAttempt.current) return;
          settlePending(granted);
          setPending(null);
          setDialogState(null);
        })
        .catch((error: unknown) => {
          if (attempt !== unlockAttempt.current) return;
          if (error instanceof AudioPreparationError) {
            setPreparationError(error.message);
            setDialogState("error");
          } else if (!params.user) {
            setDialogState("sign_in");
          } else if (params.monetization.subscriptionTier === "premium") {
            setDialogState("error");
          } else if (params.monetization.source !== "backend" || !params.monetization.adsEnabled) {
            setDialogState("error");
          } else {
            setDialogState("reward");
          }
        });
  }, [authorizeAndPrepare, params.monetization.adsEnabled, params.monetization.source, params.monetization.subscriptionTier, params.user, settlePending]);

  const prepareUnit = useCallback((request: WebRemoteAudioRequest): Promise<boolean> => {
    const attempt = ++unlockAttempt.current;
    settlePending(false);
    setPending(request);
    setPendingPurpose("prepare");
    return new Promise<boolean>((resolve) => {
      pendingResolver.current = resolve;
      attemptPreparation(request, attempt);
    });
  }, [attemptPreparation, settlePending]);

  const isUnitReady = useCallback(async (request: WebRemoteAudioRequest): Promise<boolean> => {
    const audioRefs = Array.from(new Set(
      (request.audioRefs.length > 0 ? request.audioRefs : [request.audioRef]).filter(Boolean),
    ));
    if (audioRefs.length === 0) return false;
    return missingCachedAudio(audioRefs).then((missing) => missing.length === 0).catch(() => false);
  }, []);

  const unlock = useCallback(async () => {
    if (
      !pending
      || !params.user
      || !adUnitPath
      || params.monetization.source !== "backend"
      || !params.monetization.adsEnabled
    ) {
      setDialogState("error");
      return;
    }
    if (getRewardedAdAvailability({
      adUnitPath,
      consent: params.consent,
      isPremium: params.monetization.subscriptionTier === "premium",
    }) !== "available") {
      setDialogState("error");
      return;
    }
    const attempt = ++unlockAttempt.current;
    setDialogState("working");
    try {
      const { audioRef: _audioRef, audioRefs: _audioRefs, ...unit } = pending;
      const begin = await callAuthenticatedWordinariusFunction<BeginRewardResponse>(
        "beginWebRewardedAudioUnlock",
        { ...unit, adUnitPath },
      );
      if (attempt !== unlockAttempt.current) return;
      const outcome = await showRewardedAd({
        adUnitPath,
        consent: params.consent,
        isPremium: false,
      });
      if (attempt !== unlockAttempt.current) return;
      if (outcome.result === "unavailable") {
        if (!isRewardedProviderUnavailable(outcome.reason)) {
          setDialogState("error");
          return;
        }
        setDialogState("provider_fallback");
        await callAuthenticatedWordinariusFunction("completeWebRewardedAudioNoFillFallback", {
          sessionId: begin.sessionId,
          unavailableReason: outcome.reason,
        });
        if (attempt !== unlockAttempt.current) return;
        const granted = pendingPurpose === "prepare"
          ? await authorizeAndPrepare(pending)
          : await authorizeAndPlay(pending, pendingPlayback.current);
        if (attempt !== unlockAttempt.current) return;
        settlePending(granted);
        setPending(null);
        const promotion = granted
          ? await getHousePromotion(params.supportLanguage).catch(() => null)
          : null;
        if (attempt !== unlockAttempt.current) return;
        if (promotion) {
          setHousePromotion(promotion);
          setDialogState("house_promo");
        } else {
          setDialogState(null);
        }
        return;
      }
      if (outcome.result === "closed") {
        setDialogState("not_completed");
        return;
      }
      await callAuthenticatedWordinariusFunction("completeWebRewardedAudioUnlock", {
        sessionId: begin.sessionId,
        rewardType: outcome.payload?.type || null,
        rewardAmount: outcome.payload?.amount || null,
      });
      if (attempt !== unlockAttempt.current) return;
      setLastRewardedUnlockAtMs(Date.now());
      const granted = pendingPurpose === "prepare"
        ? await authorizeAndPrepare(pending)
        : await authorizeAndPlay(pending, pendingPlayback.current);
      if (attempt !== unlockAttempt.current) return;
      settlePending(granted);
      setPending(null);
      setDialogState(null);
    } catch {
      if (attempt === unlockAttempt.current) {
        setDialogState("error");
      }
    }
  }, [adUnitPath, authorizeAndPlay, authorizeAndPrepare, params.consent, params.monetization.adsEnabled, params.monetization.source, params.monetization.subscriptionTier, params.supportLanguage, params.user, pending, pendingPurpose, settlePending]);

  const canRetryRewarded = Boolean(
    pending
    && params.user
    && adUnitPath
    && params.monetization.source === "backend"
    && params.monetization.adsEnabled
    && params.monetization.subscriptionTier !== "premium",
  );
  const rewardCopy = getWebAudioRewardCopy(params.supportLanguage, pending?.unitType || "intentHeading");

  const dialog = dialogState === "house_promo" && housePromotion ? (
    <HousePromotionDialog key={housePromotion.id} promotion={housePromotion} label={copy.housePromoLabel} closeLabel={copy.close} continueLabel={copy.housePromoContinue} onClose={closeDialog} onShown={() => markHousePromotionShown(housePromotion.id)} />
  ) : dialogState ? (
    <div className="dialog-backdrop" role="presentation">
      <section
        className={`audio-access-dialog${dialogState === "provider_fallback" ? " audio-access-dialog--fallback" : ""}${dialogState === "house_promo" ? " audio-access-dialog--house-promo" : ""}`}
        role="dialog"
        aria-live="polite"
        aria-modal="true"
        aria-busy={dialogState === "checking" || dialogState === "working" || dialogState === "provider_fallback"}
        aria-labelledby="audio-access-title"
        data-audio-preparation-error={preparationError}
      >
        <button type="button" className="icon-button dialog-close" aria-label={copy.close} onClick={closeDialog}>
          <X aria-hidden="true" />
        </button>
        {dialogState !== "provider_fallback" && dialogState !== "house_promo" ? (
          <span className="audio-access-icon" aria-hidden="true"><LockKeyhole /></span>
        ) : null}
        <h2 id="audio-access-title">
          {dialogState === "checking"
            ? copy.checkingTitle
            : dialogState === "sign_in"
              ? copy.signInTitle
              : dialogState === "reward" || dialogState === "working"
                ? rewardCopy.title
                : dialogState === "provider_fallback"
                  ? copy.providerFallback
                  : dialogState === "not_completed"
                    ? copy.notCompleted
                    : copy.error}
        </h2>
        <p>
          {dialogState === "checking"
            ? copy.checkingBody
            : dialogState === "sign_in"
              ? copy.signInBody
              : dialogState === "reward"
                ? rewardCopy.body
                : dialogState === "working"
                  ? copy.preparing
                  : dialogState === "provider_fallback"
                    ? copy.providerFallbackBody
                    : dialogState === "not_completed"
                      ? copy.notCompletedBody
                      : copy.errorBody}
        </p>
        {dialogState === "sign_in" ? (
          <button type="button" className="primary-button" onClick={() => { closeDialog(); params.onOpenProfile(); }}>
            {copy.openProfile}
          </button>
        ) : null}
        {dialogState === "reward" ? (
          <button type="button" className="primary-button" onClick={() => void unlock()}>
            <Play aria-hidden="true" />{rewardCopy.action}
          </button>
        ) : null}
        {(dialogState === "error" || dialogState === "not_completed") && (canRetryRewarded || pendingPurpose === "prepare") ? (
          <button type="button" className="primary-button" onClick={() => {
            if (pending && pendingPurpose === "prepare" && dialogState === "error") {
              attemptPreparation(pending, ++unlockAttempt.current);
            } else {
              void unlock();
            }
          }}>
            <Play aria-hidden="true" />{copy.tryAgain}
          </button>
        ) : null}
        {(dialogState === "checking" || dialogState === "working" || dialogState === "provider_fallback") ? (
          <span className="loading-ring" aria-hidden="true" />
        ) : null}
      </section>
    </div>
  ) : null;

  return { dialog, isUnitReady, lastRewardedUnlockAtMs, prepareUnit, requestPlay };
}
